import { Link } from 'react-router-dom';

export default function PricingTiers() {
  const websiteBuilds = [
    {
      name: 'Foundation Website',
      price: '$1,500+',
      description: 'Best for businesses that need a professional website done right.',
      features: [
        '5-page custom website',
        'Mobile-first design',
        'SEO-ready foundation',
        'Speed optimization',
        'Contact form + Google Maps',
        'Clear call-to-action layout',
        '2 revision rounds',
        '1–3 week turnaround',
      ],
      cta: 'Start Foundation',
    },
    {
      name: 'Growth Website',
      price: '$2,500+',
      description: 'Built for businesses focused on visibility and leads.',
      badge: 'Most Popular',
      features: [
        'Everything in Foundation, plus:',
        '8–10 pages',
        'Advanced local SEO setup',
        'Optimized service pages',
        'Conversion-focused structure',
        'Analytics setup',
        '3 revision rounds',
      ],
      cta: 'Start Growth',
      highlighted: true,
    },
    {
      name: 'Authority Website',
      price: '$4,000+',
      description: 'For established businesses that want premium positioning and long-term dominance.',
      features: [
        'Everything in Growth, plus:',
        'Custom design system',
        'Content/blog framework',
        'Advanced SEO structure',
        'Conversion optimization build',
        'Strategic roadmap',
      ],
      cta: 'Request Authority Quote',
    },
  ];

  const monthlyServices = [
    {
      name: 'Maintenance',
      price: '$125',
      period: '/month',
      note: 'Minimum 3-month commitment',
      features: [
        'Hosting',
        'Security monitoring',
        'Backups',
        'Plugin/software updates',
        'Minor content edits',
      ],
      cta: 'Add Maintenance',
    },
    {
      name: 'Local Visibility',
      price: '$275',
      period: '/month',
      description: 'Improve rankings and local visibility.',
      features: [
        'Google Business optimization',
        'Local keyword targeting (1–3 terms)',
        'On-page SEO improvements',
        'Monthly performance report',
      ],
      cta: 'Boost Visibility',
    },
    {
      name: 'Growth Plan',
      price: '$450',
      period: '/month',
      description: 'Aggressive growth + conversion optimization.',
      features: [
        'Everything in Local Visibility, plus:',
        'Expanded keyword strategy',
        'Content additions',
        'Backlink strategy',
        'Conversion improvements',
        'Monthly strategy call',
      ],
      cta: 'Start Growth Plan',
    },
  ];

  const addOns = [
    { name: 'Additional page', price: '$150–$250' },
    { name: 'Landing page', price: '$500' },
    { name: 'Blog setup', price: '$400' },
    { name: 'Copywriting', price: '$300–$1,000' },
    { name: 'Logo design', price: '$400–$800' },
    { name: 'Rush delivery (under 2 weeks)', price: '+$500' },
    { name: 'Google Ads setup', price: '$750' },
  ];

  return (
    <div className="bg-white">
      {/* Website Builds */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="font-sora font-bold text-4xl text-charcoal text-center mb-12">
            Website Builds (One-Time)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {websiteBuilds.map((tier, index) => (
              <div
                key={index}
                className={`rounded-3xl p-8 border-2 transition-all ${
                  tier.highlighted
                    ? 'bg-charcoal border-charcoal text-white shadow-xl scale-105'
                    : 'bg-white border-gray-200 hover:shadow-lg'
                }`}
              >
                {tier.badge && (
                  <div className="inline-block bg-primary text-white px-4 py-1 rounded-full font-inter text-sm font-medium mb-4">
                    {tier.badge}
                  </div>
                )}
                <h3
                  className={`font-inter font-semibold text-2xl mb-2 ${
                    tier.highlighted ? 'text-white' : 'text-charcoal'
                  }`}
                >
                  {tier.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span
                    className={`font-sora font-bold text-5xl ${
                      tier.highlighted ? 'text-white' : 'text-charcoal'
                    }`}
                  >
                    {tier.price}
                  </span>
                </div>
                <p
                  className={`font-inter text-base mb-6 ${
                    tier.highlighted ? 'text-gray-200' : 'text-gray-600'
                  }`}
                >
                  {tier.description}
                </p>

                <div className="border-t border-gray-300/30 pt-6 mb-8">
                  <h4
                    className={`font-inter font-semibold text-sm mb-4 ${
                      tier.highlighted ? 'text-white' : 'text-charcoal'
                    }`}
                  >
                    Includes:
                  </h4>
                  <ul className="space-y-3">
                    {tier.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className={`font-inter text-[15px] flex items-start gap-2 ${
                          tier.highlighted ? 'text-gray-100' : 'text-gray-700'
                        }`}
                      >
                        <i
                          className={`ri-check-line text-lg mt-0.5 ${
                            tier.highlighted ? 'text-primary' : 'text-primary'
                          }`}
                        ></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to="/contact"
                  className={`block w-full text-center px-6 py-4 rounded-xl font-inter font-medium text-base transition-colors whitespace-nowrap cursor-pointer ${
                    tier.highlighted
                      ? 'bg-primary text-white hover:bg-primary/90'
                      : 'bg-primary text-white hover:bg-primary/90'
                  }`}
                >
                  {tier.cta}
                </Link>
              </div>
            ))}
          </div>
          <p className="font-inter text-sm text-gray-500 text-center max-w-3xl mx-auto">
            Final pricing depends on page count, copywriting needs, integrations, and timeline. All projects require a 50% deposit to begin.
          </p>
        </div>
      </section>

      {/* Monthly Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="font-sora font-bold text-4xl text-charcoal text-center mb-12">
            Ongoing Services (Monthly)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {monthlyServices.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-8 border border-gray-200 hover:shadow-lg transition-shadow"
              >
                <h3 className="font-inter font-semibold text-2xl text-charcoal mb-2">
                  {service.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="font-sora font-bold text-5xl text-charcoal">
                    {service.price}
                  </span>
                  {service.period && (
                    <span className="font-inter text-lg text-gray-500">
                      {service.period}
                    </span>
                  )}
                </div>
                {service.note && (
                  <p className="font-inter text-sm text-gray-500 mb-4 italic">
                    {service.note}
                  </p>
                )}
                {service.description && (
                  <p className="font-inter text-base text-gray-600 mb-6">
                    {service.description}
                  </p>
                )}

                <div className="border-t border-gray-200 pt-6 mb-8">
                  <h4 className="font-inter font-semibold text-sm text-charcoal mb-4">
                    Includes:
                  </h4>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className="font-inter text-[15px] text-gray-700 flex items-start gap-2"
                      >
                        <i className="ri-check-line text-primary text-lg mt-0.5"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to="/contact"
                  className="block w-full bg-primary text-white text-center px-6 py-4 rounded-xl font-inter font-medium text-base hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {service.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-Ons */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="font-sora font-bold text-4xl text-charcoal text-center mb-12">
            Add-Ons (When Needed)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
            {addOns.map((addon, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-4 border-b border-gray-200"
              >
                <span className="font-inter text-base text-gray-700">
                  {addon.name}
                </span>
                <span className="font-inter font-semibold text-base text-charcoal whitespace-nowrap">
                  {addon.price}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}